import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./icons.js";

/**
 * B667 (URGENT, frame 551:136) — the house RIGHT SHEET: a 560-wide panel
 * docked to the right edge, full height, over a scrim that dims the content
 * column, with a head (kicker over title, one teaching line, ✕), a body that
 * scrolls, and a foot pinned under it holding the doors (left) and the
 * sheet's state line (right, amber).
 *
 * Pathik 2026-10-06, on B611's centered `Dialog wide`: "these edit modals
 * look horrible. what happened to the drawer you designed for these edits?"
 * This is that drawer, built once in the package so every editor-in-a-sheet
 * composes it (the 2026-10-02 ruling: "drawer/modal/inline right below the
 * row"). `Dialog` stays for confirmations — a question with two answers.
 *
 * Escape, ✕ and a press on the scrim all call `onClose`; the CALLER decides
 * whether that closes or asks first (an editor with changes asks in its
 * foot). Opening moves focus into the sheet so the keyboard starts there.
 */
export function RightSheet(props: {
  readonly id: string;
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  /** The act or the card this sheet edits, over the title ("What buyers see"). */
  readonly kicker?: string | undefined;
  /** One teaching sentence under the title (R10). */
  readonly lede?: ReactNode;
  /** B386 — the sheet's name in a redline sidecar. */
  readonly redline?: string | undefined;
  /** B697 — the wider sheet a long editor draws (560:324 is 600; 551:136 is the 560 default). */
  readonly wide?: boolean | undefined;
  /** The foot's doors, left (SAVE · CANCEL). Absent with no status = no foot. */
  readonly actions?: ReactNode;
  /** The foot's state line, right ("1 photo added · not saved yet"). */
  readonly status?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLElement>(null);
  const onClose = useRef(props.onClose);
  onClose.current = props.onClose;
  useEffect(() => {
    if (!props.open) return undefined;
    ref.current?.focus();
    /* Escape closes from anywhere while the sheet is up — the focus may be on
       a field, the scrim or the page behind it. */
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props.open]);
  if (!props.open) return null;
  const titleId = `${props.id}-title`;
  const hasFoot = props.actions !== undefined || (props.status !== undefined && props.status !== null);
  return (
    <>
      <div
        className="t-rsheet-scrim"
        aria-hidden="true"
        {...(props.redline !== undefined ? { "data-redline": `${props.redline}-scrim` } : {})}
        onClick={props.onClose}
      />
      <aside
        id={props.id}
        ref={ref}
        className={props.wide === true ? "t-rsheet t-rsheet--wide" : "t-rsheet"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
      >
        <header className="t-rsheet-head">
          {props.kicker === undefined ? null : <p className="t-rsheet-kicker">{props.kicker}</p>}
          <h2 className="t-rsheet-title" id={titleId}>
            {props.title}
          </h2>
          {props.lede === undefined ? null : <p className="t-rsheet-lede">{props.lede}</p>}
          <button type="button" className="t-rsheet-close" aria-label="Close" onClick={props.onClose}>
            <Icon name="close" size={14} />
          </button>
        </header>
        <div className="t-rsheet-body">{props.children}</div>
        {hasFoot ? (
          <footer className="t-rsheet-foot">
            {props.actions === undefined ? null : <div className="t-rsheet-doors">{props.actions}</div>}
            {props.status === undefined || props.status === null ? null : (
              <p className="t-rsheet-status" role="status">
                {props.status}
              </p>
            )}
          </footer>
        ) : null}
      </aside>
    </>
  );
}
